import React, { useEffect, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Pagination, CircularProgress, Button, Chip, Typography, Alert,
} from '@mui/material';
import { fetchCustomerOrders } from '../api';
import { formatDate, headerCell, money, surface } from '../format';
import ViewActivityDialog from '../../order-list/ViewActivityDialog';

const OrdersTab = ({ customerId }) => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activityOrder, setActivityOrder] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const res = await fetchCustomerOrders(customerId, { page, limit: 10 });
                if (cancelled) return;
                setRows(res.data.data || []);
                setTotalPages(res.data.totalPages || 1);
                setTotal(res.data.total || 0);
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.message || 'Failed to load orders');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [customerId, page]);

    return (
        <Box>
            <Typography sx={{ color: '#6b7280', fontSize: '0.8125rem', mb: 1.5 }}>
                {total} order{total === 1 ? '' : 's'}
            </Typography>
            <Paper elevation={0} sx={surface}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['ID', 'Name', 'Amount', 'Status', 'Gateway', 'Created', ''].map((h) => (
                                        <TableCell key={h} sx={headerCell} align={h === '' ? 'right' : 'left'}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#6b7280' }}>
                                            No orders yet.
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map((order) => {
                                    const hasActivity = !!order.notes?.viewed_datasets?.length;
                                    return (
                                        <TableRow key={order.id} hover>
                                            <TableCell>{order.id}</TableCell>
                                            <TableCell>{order.name || '—'}</TableCell>
                                            <TableCell>{money(order.amount, order.currency)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={order.status || '—'}
                                                    sx={{ textTransform: 'capitalize', bgcolor: '#f3f4f6' }}
                                                />
                                            </TableCell>
                                            <TableCell>{order.payment_gateway || '—'}</TableCell>
                                            <TableCell sx={{ color: '#6b7280' }}>{formatDate(order.createdAt)}</TableCell>
                                            <TableCell align="right">
                                                {hasActivity && (
                                                    <Button
                                                        size="small"
                                                        sx={{ textTransform: 'none' }}
                                                        onClick={() => setActivityOrder(order)}
                                                    >
                                                        Activity
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} shape="rounded" />
                </Box>
            )}
            <ViewActivityDialog
                open={Boolean(activityOrder)}
                onClose={() => setActivityOrder(null)}
                order={activityOrder}
            />
        </Box>
    );
};

export default OrdersTab;
